/**
 * Gentoo (젠투) JavaScript SDK 통합 헬퍼.
 *
 * SDK 스펙: https://gentoo.gitbook.io/gentoo/self-hosted/sdk-javascript
 *
 * 사용 방식
 * - SDK 스크립트 로드는 `GentooInitializer` (AppProviders 하위) 가 담당.
 * - Boot 파라미터(`displayLocation`, `itemId`) 는 `FloatingConfig`
 *   `zento` prop 으로 페이지에서 전달.
 * - 플로팅 UI 는 프로젝트 자체 `<ZentoButton />` 을 그대로 사용하며,
 *   `isCustomButton: true, showGentooButton: false` 로 init 하고
 *   버튼에 `GENTOO_CUSTOM_BUTTON_ID` 를 부착해 SDK 가 클릭을 잡도록 한다.
 * - 페이지 커스텀 렌더러 (`FloatingConfig.zento={{ render }}`) 도
 *   반환하는 트리거 엘리먼트에 반드시 `id={GENTOO_CUSTOM_BUTTON_ID}` 를
 *   포함해야 SDK 클릭 바인딩이 동작한다.
 */

import { generateUuid } from '@/shared/utils/generateUuid';

/** SDK 가 클릭 바인딩하는 커스텀 버튼 ID (SDK 스펙 고정). */
export const GENTOO_CUSTOM_BUTTON_ID = 'gentoo-custom-button';

/** 콘솔에 등록된 파트너 External Key. */
export const GENTOO_PARTNER_ID =
  process.env.NEXT_PUBLIC_GENTOO_PARTNER_ID ?? '';

export type GentooDisplayLocation =
  | 'HOME'
  | 'PRODUCT_LIST'
  | 'PRODUCT_DETAIL'
  | 'OTHERS';

export type GentooBootParams = {
  partnerId: string;
  authCode: string;
  udid?: string;
  displayLocation?: GentooDisplayLocation;
  itemId?: string;
};

export type GentooInitParams = {
  isCustomButton?: boolean;
  showGentooButton?: boolean;
};

type GentooMethod = 'boot' | 'init' | 'unmount' | 'getFormSubmittedEvent';
type GentooIO = (method: GentooMethod, params: Record<string, unknown>) => void;

declare global {
  interface Window {
    GentooIO?: GentooIO;
  }
}

export function callGentoo(
  method: GentooMethod,
  params: Record<string, unknown>,
): void {
  if (typeof window === 'undefined') return;
  window.GentooIO?.(method, params);
}

/**
 * 상담 폼 제출 콜백 등록.
 * SDK 스펙: https://gentoo.gitbook.io/gentoo/self-hosted/sdk-javascript/advanced/callback
 *
 * 가이드상 사전 질문 답변이 `{ p1: { key, answer }, p2: … }` 형태로 넘어온다고 안내받았으나
 * 실제 페이로드는 미확인 상태다. 타입을 좁히지 않고 원문 그대로 호출부에 넘긴다.
 */
export function onGentooFormSubmitted(
  callback: (params: unknown) => void,
): void {
  callGentoo('getFormSubmittedEvent', { callback });
}

const AUTH_CODE_STORAGE_KEY = 'gentoo:authCode';
let cachedAuthCode: string | null = null;

/**
 * 비회원 포함 사용자 식별용 authCode.
 * 로그인 컨텍스트가 붙기 전까지 브라우저별 UUID 로 대체한다
 * (SDK 스펙: "비회원의 경우에도 난수를 생성하여 보내주세요.").
 */
export function getGentooAuthCode(): string {
  if (cachedAuthCode) return cachedAuthCode;
  if (typeof window === 'undefined') return '';
  try {
    const stored = window.localStorage.getItem(AUTH_CODE_STORAGE_KEY);
    if (stored) {
      cachedAuthCode = stored;
      return stored;
    }
    const next = generateUuid();
    window.localStorage.setItem(AUTH_CODE_STORAGE_KEY, next);
    cachedAuthCode = next;
    return next;
  } catch {
    // 프라이빗 모드 등 storage 접근 실패 — 세션 한정 난수로 폴백
    const next = generateUuid();
    cachedAuthCode = next;
    return next;
  }
}
